import { track, identify, Identify } from './amplitude';
import {
  ERROR_OCCURRED,
  UNHANDLED_REJECTION,
  WEBGL_CONTEXT_LOST,
  FPS_SNAPSHOT,
  SCENE_PERFORMANCE_SUMMARY,
  LOW_BATTERY_WARNING,
  MEMORY_SNAPSHOT,
} from './events';
import { $sceneId } from '../state/appState';
import { sceneLoadingReset, sceneLoadingStarted } from '../state/sceneLoading';
import { recordWebglContextLost, recordWebglContextRestored } from '../debug/memoryTracker';

type NavigatorWithBattery = Navigator & {
  getBattery?: () => Promise<{
    level: number;
    charging: boolean;
    addEventListener: (type: string, listener: () => void) => void;
  }>;
};

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
};

const FPS_SAMPLE_INTERVAL_MS = 60_000;
const SLOW_FRAME_MS = 33;          // ≥ ~30fps drop
const VERY_SLOW_FRAME_MS = 50;     // ≥ ~20fps drop
const BATTERY_LOW_THRESHOLD = 0.15;

/**
 * Sustained-low-fps detection (для `episodes_count` в per-scene aggregate).
 * Считаем avg по скользящему окну `EPISODE_WINDOW_MS`; вход в эпизод по
 * нижнему threshold, выход по верхнему (гистерезис против flicker'а на
 * границе). Минимум `EPISODE_MIN_FRAMES` в окне нужен, чтобы avg был
 * репрезентативным — иначе на старте сцены первый просевший кадр сразу
 * триггерил бы эпизод.
 */
const EPISODE_WINDOW_MS = 3_000;
const EPISODE_LOW_FPS = 25;
const EPISODE_RECOVERY_FPS = 30;
const EPISODE_MIN_FRAMES = 30;

type IntervalAccumulator = {
  frames: number;
  totalTimeMs: number;
  slowFrames: number;
  maxFrameMs: number;
};

const createIntervalAcc = (): IntervalAccumulator => ({
  frames: 0,
  totalTimeMs: 0,
  slowFrames: 0,
  maxFrameMs: 0,
});

type SceneAccumulator = {
  sceneId: string | null;
  startedAt: number;
  frames: number;
  totalTimeMs: number;
  slowFrames: number;
  verySlowFrames: number;
  maxFrameMs: number;
  episodesCount: number;
};

let intervalAcc: IntervalAccumulator = createIntervalAcc();
let sceneAcc: SceneAccumulator | null = null;

// Episode-detection sliding window (per active scene).
let episodeWindow: number[] = [];
let episodeWindowSum = 0;
let inEpisode = false;

const resetEpisodeWindow = (): void => {
  episodeWindow = [];
  episodeWindowSum = 0;
  inEpisode = false;
};

let fpsLastTimestamp = 0;
let fpsAnimationId: number | null = null;
let fpsIntervalId: ReturnType<typeof setInterval> | null = null;

const trackJsErrors = (): void => {
  window.addEventListener('error', (event) => {
    track(ERROR_OCCURRED, {
      message: event.message,
      filename: event.filename,
      line: event.lineno,
      col: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    track(UNHANDLED_REJECTION, {
      reason: String(event.reason),
    });
  });
};

/**
 * Reports the context of `canvas` being lost. Returns the unsubscribe: a host
 * that loses its own context on purpose (`forceContextLoss`, `loseContext`)
 * calls it first, or every teardown reads as a crash.
 */
export const trackWebGlContextLost = (canvas: HTMLCanvasElement): (() => void) => {
  const onLost = (): void => {
    recordWebglContextLost();
    track(WEBGL_CONTEXT_LOST, {
      scene_id: $sceneId.getState(),
    });
  };
  const onRestored = (): void => {
    recordWebglContextRestored();
  };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);
  return () => {
    canvas.removeEventListener('webglcontextlost', onLost);
    canvas.removeEventListener('webglcontextrestored', onRestored);
  };
};

const onFrame = (timestamp: number): void => {
  if (fpsLastTimestamp > 0) {
    const delta = timestamp - fpsLastTimestamp;
    if (delta > 0) {
      intervalAcc.frames += 1;
      intervalAcc.totalTimeMs += delta;
      if (delta > SLOW_FRAME_MS) intervalAcc.slowFrames += 1;
      if (delta > intervalAcc.maxFrameMs) intervalAcc.maxFrameMs = delta;

      if (sceneAcc !== null) {
        sceneAcc.frames += 1;
        sceneAcc.totalTimeMs += delta;
        if (delta > SLOW_FRAME_MS) sceneAcc.slowFrames += 1;
        if (delta > VERY_SLOW_FRAME_MS) sceneAcc.verySlowFrames += 1;
        if (delta > sceneAcc.maxFrameMs) sceneAcc.maxFrameMs = delta;

        episodeWindow.push(delta);
        episodeWindowSum += delta;
        while (episodeWindowSum > EPISODE_WINDOW_MS && episodeWindow.length > 0) {
          episodeWindowSum -= episodeWindow.shift() as number;
        }
        if (episodeWindow.length >= EPISODE_MIN_FRAMES) {
          const avgFpsInWindow = (episodeWindow.length * 1000) / episodeWindowSum;
          if (!inEpisode && avgFpsInWindow < EPISODE_LOW_FPS) {
            inEpisode = true;
            sceneAcc.episodesCount += 1;
          } else if (inEpisode && avgFpsInWindow > EPISODE_RECOVERY_FPS) {
            inEpisode = false;
          }
        }
      }
    }
  }
  fpsLastTimestamp = timestamp;
  fpsAnimationId = requestAnimationFrame(onFrame);
};

const onInterval = (): void => {
  const acc = intervalAcc;
  intervalAcc = createIntervalAcc();
  if (acc.frames === 0) return;

  const avgFps = +((acc.frames / acc.totalTimeMs) * 1000).toFixed(1);
  const slowFramesPct = +(((acc.slowFrames / acc.frames) * 100).toFixed(1));

  track(FPS_SNAPSHOT, {
    avg_fps: avgFps,
    slow_frames_pct: slowFramesPct,
    max_frame_ms: Math.round(acc.maxFrameMs),
    scene_id: $sceneId.getState(),
  });
};

const startSceneAccumulator = (): void => {
  // Гарантия от незакрытого аккумулятора предыдущей сцены (порядок
  // sceneLoadingReset → sceneLoadingStarted нормально это закрывает; защищаемся).
  if (sceneAcc !== null) flushSceneAccumulator();

  const sid = $sceneId.getState();
  sceneAcc = {
    sceneId: sid == null ? null : String(sid),
    startedAt: performance.now(),
    frames: 0,
    totalTimeMs: 0,
    slowFrames: 0,
    verySlowFrames: 0,
    maxFrameMs: 0,
    episodesCount: 0,
  };
  resetEpisodeWindow();
};

function flushSceneAccumulator(): void {
  if (sceneAcc === null) return;
  const acc = sceneAcc;
  sceneAcc = null;
  resetEpisodeWindow();

  if (acc.frames === 0) return;

  const durationMs = Math.round(performance.now() - acc.startedAt);
  const avgFps = +((acc.frames / acc.totalTimeMs) * 1000).toFixed(1);
  const slowFramesPct = +(((acc.slowFrames / acc.frames) * 100).toFixed(1));
  const verySlowFramesPct = +(((acc.verySlowFrames / acc.frames) * 100).toFixed(1));

  track(SCENE_PERFORMANCE_SUMMARY, {
    scene_id: acc.sceneId,
    duration_ms: durationMs,
    total_frames: acc.frames,
    avg_fps: avgFps,
    slow_frames_pct: slowFramesPct,
    very_slow_frames_pct: verySlowFramesPct,
    max_frame_ms: Math.round(acc.maxFrameMs),
    episodes_count: acc.episodesCount,
  });
}

const startFpsMonitoring = (): void => {
  fpsAnimationId = requestAnimationFrame(onFrame);
  fpsIntervalId = setInterval(onInterval, FPS_SAMPLE_INTERVAL_MS);
};

const trackBattery = (): void => {
  const nav = navigator as NavigatorWithBattery;
  if (typeof nav.getBattery !== 'function') return;

  nav.getBattery().then((battery) => {
    const userProperties = new Identify();
    userProperties.set('battery_level', battery.level);
    userProperties.set('battery_charging', battery.charging);
    identify(userProperties);

    battery.addEventListener('levelchange', () => {
      if (battery.level < BATTERY_LOW_THRESHOLD && !battery.charging) {
        track(LOW_BATTERY_WARNING, {
          level: battery.level,
          scene_id: $sceneId.getState(),
        });
      }
    });
  }).catch(() => {
    // Battery API not available or denied
  });
};

const trackMemorySnapshot = (): void => {
  const perf = performance as PerformanceWithMemory;
  if (!perf.memory) return;

  track(MEMORY_SNAPSHOT, {
    used_mb: +(perf.memory.usedJSHeapSize / (1024 * 1024)).toFixed(1),
    total_mb: +(perf.memory.totalJSHeapSize / (1024 * 1024)).toFixed(1),
    limit_mb: +(perf.memory.jsHeapSizeLimit / (1024 * 1024)).toFixed(1),
  });
};

export const startPerformanceTracking = (): void => {
  trackJsErrors();
  startFpsMonitoring();
  trackBattery();
  trackMemorySnapshot();

  sceneLoadingStarted.watch(() => {
    startSceneAccumulator();
  });

  sceneLoadingReset.watch(() => {
    flushSceneAccumulator();
  });

  window.addEventListener('pagehide', () => {
    flushSceneAccumulator();
  });
};

export const stopPerformanceTracking = (): void => {
  if (fpsAnimationId !== null) {
    cancelAnimationFrame(fpsAnimationId);
    fpsAnimationId = null;
  }
  if (fpsIntervalId !== null) {
    clearInterval(fpsIntervalId);
    fpsIntervalId = null;
  }
};
